import { fromPairs } from 'lodash';
import { useCallback, useMemo } from 'react';
import { get, useFormContext, useFormState } from 'react-hook-form';
import { type MixedSchema } from 'yup';
import { useFormAugmentedContext } from '../form/augmented/FormAugmentedContext.tsx';
import { useFormSectionEnabled } from '../form/section/FormSectionContext.tsx';
import { batchedUnregister } from '../hooks/batchedUnregister.ts';
import { useFormSetValue } from '../hooks/useFormSetValue.ts';
import { useTriggerComputedSync } from '../hooks/useTriggerComputedSync.ts';
import { useResolvedFieldState } from '../resolved-schema/useResolvedFieldState.ts';
import { getAugmentedSchema } from './getAugmentedSchema.ts';

type ISetValueOptions = Partial<{
  shouldDirty: boolean;
  shouldValidate: boolean;
  shouldTouch: boolean;
}>;

/**
 * Hook per ritornare i dettagli di un campo di input.
 *
 * Le proprietà derivate dallo schema (`visible`, `enabled`, `layoutEnabled`,
 * `required`, `warning`) sono lette da uno store centralizzato con
 * subscription per-path e proxy getter: il re-render avviene SOLO quando
 * una proprietà effettivamente acceduta dal consumer cambia.
 */
export function useField<T>(name: string | string[] | undefined) {
  const { errors, dirtyFields, touchedFields, isSubmitted } = useFormState({
    name,
  });
  const methods = useFormContext();
  const augmented = useFormAugmentedContext();
  const sectionEnabled = useFormSectionEnabled();
  const names = useMemo(
    () => (Array.isArray(name) ? name : name == null ? [] : [name]),
    [name],
  );

  // Derived state dallo store centralizzato (per-path subscription + proxy)
  const fieldStates = useResolvedFieldState(names);

  const isFieldStateStale = useCallback(
    () => augmented.resolvedStore.isStale,
    [augmented],
  );

  const error = names.map(n => get(errors, n)).filter(e => !!e);
  const touched = names.map(n => get(touchedFields, n)).filter(e => !!e);
  const dirty = names.map(n => get(dirtyFields, n)).filter(e => !!e);

  // Destrutturiamo referenze stabili: `methods` da useFormContext() cambia
  // ad ogni formState update (FormProvider useMemo include formState nei deps),
  // ma control, unregister e setValue sono funzioni stabili create una volta in useForm.
  const { control, unregister: rhfUnregister } = methods;
  const fieldSetValue = useFormSetValue();
  const triggerSync = useTriggerComputedSync();

  const setFieldValue = useCallback(
    (value: T, options: ISetValueOptions = { shouldDirty: true }) => {
      if (!name) return;
      if (Array.isArray(name)) {
        // Effettuiamo un batch update su più campi (campo multiplo): chiamiamo
        // setValue per ogni campo, ma disabilitiamo il triggerSync interno.
        // Alla fine triggeriamo manualmente una singola volta.
        for (const n of name) {
          fieldSetValue(n, value, {
            ...options,
            shouldSyncComputedValues: false,
          });
        }
        // La modifica parte da N campi insieme, quindi non esiste "il" path
        // originante: `name[0]` è una scelta arbitraria ma innocua, perché
        // l'intero gruppo è già allineato allo stesso `value` e le live
        // transform a valle non hanno differenze da propagare.
        triggerSync({ path: name[0], value });
      } else {
        fieldSetValue(name, value, options);
      }
    },
    [name, fieldSetValue, triggerSync],
  );

  const getValues = useCallback((): Record<string, any> | undefined => {
    if (!names) return undefined;

    return fromPairs(names.map(n => [n, methods.getValues(n)]));
  }, [name, methods.getValues]);

  const getValue = useCallback(<R extends T>(): R | undefined => {
    if (!name) return undefined;
    if (Array.isArray(name)) {
      // biome-ignore format: <tbd>
      console.warn(`[useField] Impossibile recuperare valore da campi multipli. Utilizzare getValues() invece.`, {name});
      return undefined;
    }
    return methods.getValues(name);
  }, [name, methods.getValues]);

  const unregister = useCallback(() => {
    if (!name) return;
    batchedUnregister(control, rhfUnregister, name);
  }, [control, rhfUnregister, name]);

  /**
   * Ottiene lo schema aggiornato con i valori attuali del form, per il campo
   * indicato. Funzione on-demand: non triggera re-render.
   */
  const getSchema = useCallback(
    (index = 0) =>
      getAugmentedSchema(
        augmented,
        methods.getValues(),
        names[index],
      ) as MixedSchema | null,
    [augmented, methods.getValues, names],
  );

  return {
    error,
    dirty,
    touched,
    isSubmitted,
    getSchema,
    visible: fieldStates.every(s => s.visible),
    /** È editabile solo se tutti gli schemi dicono che è editabile (true or undefined)*/
    enabled:
      sectionEnabled === false ? false : fieldStates.every(s => s.enabled),
    /** Indica se il campo è editabile, ma viene utilizzato solo dai componenti a livello di UI/UX e non ha impatto sulla validazione (e.g. permette modifiche) */
    layoutEnabled:
      sectionEnabled === false
        ? false
        : fieldStates.every(s => s.layoutEnabled),
    required: fieldStates.some(s => s.required),
    warnings: fieldStates
      .map(s => s.warning)
      .filter((w): w is string => w !== null),
    /** Imposta il valore del campo a `value`. Non funziona quando l'hook è utilizzato su campi multipli. */
    setValue: setFieldValue,
    /** Ritorna il valore del campo indicato. Per recuperare campi multipli, utilizzare getValues().*/
    getValue,
    /** Ritorna i valore di tutti i nomi passati come campi */
    getValues,
    unregister,
    isFieldStateStale,
  };
}

export type IUseField = ReturnType<typeof useField>;
